import { Capacitor } from "@capacitor/core";

import { safeConsole } from "@/utils/safeConsole";
// Type-only: erased at build, so this creates no runtime edge to the native
// module and none of its imports reach the entry chunk.
import type {
  NativeCallKitContext,
  NativeDirectOutboundOptions,
} from "./callKitNative";

export type { NativeDirectOutboundOptions } from "./callKitNative";

/**
 * Web-safe entry point for the native lock-screen calling layer (SD-16321).
 *
 * Mirrors the dispatch-push façade split (`pushNotifications.ts`, SD-13244):
 * the real implementation lives in `callKitNative.ts` and is reached only
 * through the gated `import()` below, because the SamedayCallKit Capacitor
 * plugin has no web implementation — a web-path method call would reject —
 * and because `@store/session` (an entry-graph module) imports this façade
 * for the logout teardown.
 *
 * Keep this file free of anything but the platform check.
 */

type NativeCallKit = Pick<
  typeof import("./callKitNative"),
  | "configureNativeCallKit"
  | "teardownNativeCallKit"
  | "nativeCallControl"
  | "getActiveNativeCall"
  | "getNativeCallSlotOccupied"
  | "subscribeNativeCall"
  | "startOutboundNativeCall"
  | "cancelOutboundNativeCall"
  | "startDirectOutboundNativeCall"
  | "cancelDirectOutboundNativeCall"
>;

/**
 * The native call-bridge primitives (SD-17313), resolved only inside the iOS
 * shell. `null` on web so the caller skips wiring the bridge entirely.
 */
export type NativeCallBridgePrimitives = {
  control: import("@libs/Phone/nativeCall").NativeCallControl;
  getActiveCall: () => Promise<import("@/types").JsonValue>;
  subscribe: (
    event: string,
    callback: (data: import("@/types").JsonValue) => void,
  ) => Promise<() => void>;
};

/**
 * Native's own view of whether CallKit is on a call. `"unknown"` is a real
 * third answer — the bridge could not be reached — and must never be collapsed
 * into `"idle"` by a caller deciding whether it is safe to tear the app down.
 */
export type NativeCallState = "active" | "idle" | "unknown";

export type CallKitFacadeDependencies = {
  getPlatform: () => string;
  loadNative: () => Promise<NativeCallKit>;
  logger: Pick<typeof safeConsole, "warn">;
};

export function createCallKitFacade({
  getPlatform,
  loadNative,
  logger,
}: CallKitFacadeDependencies) {
  /**
   * Hand the native layer its answer-from-killed context. No-op on web.
   * Never rejects: a failed configure only costs lock-screen ringing until
   * the next attempt (the web dock still handles foreground calls).
   */
  const configureNativeCallKit = async (
    context: NativeCallKitContext,
  ): Promise<void> => {
    if (getPlatform() !== "ios") return;
    try {
      const native = await loadNative();
      await native.configureNativeCallKit(context);
    } catch (error) {
      logger.warn(
        "application-lifecycle",
        "Native CallKit configure failed",
        error,
      );
    }
  };

  /**
   * Stop answering pushes for the signed-out (or unseated) user. No-op on
   * web. Never rejects: callers fire this with a bare `void` on the logout
   * path, so a failed chunk load must not surface as an unhandled rejection.
   */
  const teardownNativeCallKit = async (): Promise<void> => {
    if (getPlatform() !== "ios") return;
    try {
      const native = await loadNative();
      await native.teardownNativeCallKit();
    } catch (error) {
      logger.warn(
        "application-lifecycle",
        "Native CallKit teardown failed",
        error,
      );
    }
  };

  /**
   * Resolve the native call-bridge primitives (SD-17313), or null on web. Never
   * rejects: a failed load only costs the in-app view of a native call (the
   * lock-screen CallKit UI still works), so the caller simply skips wiring.
   */
  const loadNativeCallControls =
    async (): Promise<NativeCallBridgePrimitives | null> => {
      if (getPlatform() !== "ios") return null;
      try {
        const native = await loadNative();
        return {
          control: native.nativeCallControl,
          getActiveCall: native.getActiveNativeCall,
          subscribe: native.subscribeNativeCall,
        };
      } catch (error) {
        logger.warn(
          "application-lifecycle",
          "Native call bridge load failed",
          error,
        );
        return null;
      }
    };

  /**
   * Prepare native outbound delivery: report a CallKit outgoing call to `to`,
   * connect and authenticate the Telnyx socket, and arm adoption of dale's
   * ring-back (matched by X-Sameday-Outbound-Intent-Id) on native (SD-17342).
   * The promise resolves only once that socket is ready, so its caller may then
   * safely ask dale to originate. No-op on web.
   *
   * Unlike `configureNativeCallKit`/`teardownNativeCallKit`, this does NOT
   * catch-and-warn: starting an outbound call is a live user action the
   * caller is awaiting, so a native failure must propagate so the caller can
   * surface it (e.g. abort the dial attempt and show an error) instead of
   * silently doing nothing.
   */
  const startOutboundCall = async (options: {
    to: string;
    outboundIntentId: string;
  }): Promise<void> => {
    if (getPlatform() !== "ios") return;
    const native = await loadNative();
    await native.startOutboundNativeCall(options);
  };

  /**
   * Best-effort, attempt-scoped cancel of an in-flight native outbound call
   * (SD-17342). The intent id prevents a delayed failure from an
   * older dial from terminating its replacement. No-op on web.
   *
   * Unlike `startOutboundCall`, this DOES catch-and-warn: by the time a
   * caller reaches for this, the dock owns any user-visible outcome. A failed
   * chunk load here must not compound it with a second error. Native consumes
   * the intent on adoption, so a cancel
   * racing an already-ended or connected call is a safe no-op.
   */
  const cancelOutboundCall = async (
    outboundIntentId: string,
  ): Promise<void> => {
    if (getPlatform() !== "ios") return;
    try {
      const native = await loadNative();
      await native.cancelOutboundNativeCall({ outboundIntentId });
    } catch (error) {
      logger.warn(
        "application-lifecycle",
        "Native cancel outbound call failed",
        error,
      );
    }
  };

  /** Direct native origination. Start failures belong to the awaiting caller. */
  const startDirectOutboundCall = async (
    options: NativeDirectOutboundOptions,
  ): Promise<void> => {
    if (getPlatform() !== "ios") return;
    const native = await loadNative();
    await native.startDirectOutboundNativeCall(options);
  };

  /**
   * Whether native owns a call, as an occupancy question rather than a
   * mirroring one (SD-19983). Always `"idle"` on web, which has no native
   * layer to be on a call.
   *
   * The update gate asks this before taking the app away: a call answered from
   * the lock screen while the WebView was suspended has no store mirror yet, so
   * the process-local `buildReloadSafety` blockers cannot see it.
   *
   * It reads `callSlotOccupied`, NOT the call snapshot, and the difference is
   * load-bearing. The snapshot describes a call the dock can render, so it is
   * empty until the Telnyx call exists — which on the push path is after the
   * seat token has been minted and the SIP session attached, a network round
   * trip during which CallKit is already ringing the user. Reading "no call"
   * there and tearing the app down would take the controls off a call that is
   * about to connect.
   *
   * Anything other than a definite `true`/`false` answers `"unknown"`, never
   * `"idle"`: a failure, an unreachable bridge, or a shell too old to report
   * occupancy is not evidence of an idle line. Callers defer and ask again.
   */
  // Callers poll this while they wait out a call, so a bridge that is broken
  // rather than busy would otherwise repeat one breadcrumb until it crowded
  // out everything else worth having. The first failure is the diagnostic.
  let callStateFailureLogged = false;

  const getNativeCallState = async (): Promise<NativeCallState> => {
    if (getPlatform() !== "ios") return "idle";
    try {
      const native = await loadNative();
      const occupied = await native.getNativeCallSlotOccupied();
      if (occupied === true || occupied === false) {
        // Reset on a definite answer so a NEW failure after a working stretch
        // is still reported; the one-shot below only silences a repeating one.
        callStateFailureLogged = false;
        return occupied ? "active" : "idle";
      }
      // Reachable without throwing: a shell too old to report occupancy. The
      // caller will defer forever on this, which is the safe direction and
      // otherwise completely invisible, so it gets the same one-shot.
      if (!callStateFailureLogged) {
        callStateFailureLogged = true;
        logger.warn(
          "application-lifecycle",
          "Native shell does not report call occupancy; update gate will not enforce",
        );
      }
      return "unknown";
    } catch (error) {
      if (!callStateFailureLogged) {
        callStateFailureLogged = true;
        logger.warn(
          "application-lifecycle",
          "Native call-state probe failed",
          error,
        );
      }
      return "unknown";
    }
  };

  /** Best-effort cancellation, scoped to the caller's exact direct attempt. */
  const cancelDirectOutboundCall = async (attemptId: string): Promise<void> => {
    if (getPlatform() !== "ios") return;
    try {
      const native = await loadNative();
      await native.cancelDirectOutboundNativeCall({ attemptId });
    } catch (error) {
      logger.warn(
        "application-lifecycle",
        "Native cancel direct outbound call failed",
        error,
      );
    }
  };

  return {
    configureNativeCallKit,
    teardownNativeCallKit,
    loadNativeCallControls,
    getNativeCallState,
    startOutboundCall,
    cancelOutboundCall,
    startDirectOutboundCall,
    cancelDirectOutboundCall,
  };
}

const callKitFacade = createCallKitFacade({
  getPlatform: () => Capacitor.getPlatform(),
  loadNative: () => import("./callKitNative"),
  logger: safeConsole,
});

export const {
  configureNativeCallKit,
  teardownNativeCallKit,
  loadNativeCallControls,
  getNativeCallState,
  startOutboundCall,
  cancelOutboundCall,
  startDirectOutboundCall,
  cancelDirectOutboundCall,
} = callKitFacade;
